import { useState } from 'react';

export default function FeaturesSettings() {
  const [features, setFeatures] = useState([
    {
      id: 1,
      icon: 'ri-truck-line',
      title: 'Free Shipping',
      description: 'On orders over N30,000'
    },
    {
      id: 2,
      icon: 'ri-arrow-go-back-line',
      title: 'Easy Returns',
      description: '3-day return policy'
    },
    {
      id: 3,
      icon: 'ri-shield-check-line',
      title: 'Secure Payment',
      description: '100% secure transactions'
    },
    {
      id: 4,
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Dedicated customer service'
    }
  ]);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(null);

  const popularIcons = [
    'ri-truck-line',
    'ri-arrow-go-back-line',
    'ri-shield-check-line',
    'ri-customer-service-2-line',
    'ri-gift-line',
    'ri-star-line',
    'ri-heart-line',
    'ri-shopping-bag-line',
    'ri-price-tag-3-line',
    'ri-medal-line',
    'ri-time-line',
    'ri-global-line'
  ];

  const handleFeatureChange = (id, field, value) => {
    setFeatures(features.map(f => 
      f.id === id ? { ...f, [field]: value } : f
    ));
  };

  const handleIconSelect = (id, icon) => {
    handleFeatureChange(id, 'icon', icon);
    setShowIconPicker(null);
  };

  const handleSave = () => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleReset = () => {
    setFeatures([
      {
        id: 1,
        icon: 'ri-truck-line',
        title: 'Free Shipping',
        description: 'On orders over N30,000'
      },
      {
        id: 2,
        icon: 'ri-arrow-go-back-line',
        title: 'Easy Returns',
        description: '3-day return policy'
      },
      {
        id: 3,
        icon: 'ri-shield-check-line',
        title: 'Secure Payment',
        description: '100% secure transactions'
      },
      {
        id: 4,
        icon: 'ri-customer-service-2-line',
        title: '24/7 Support',
        description: 'Dedicated customer service'
      }
    ]);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Features Section</h2>
        <p className="text-sm text-gray-600">Customize the features displayed on your homepage</p>
      </div>

      {showSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
          <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
          <span className="text-sm text-green-800 font-medium">Features saved successfully!</span>
        </div>
      )}

      <div className="space-y-6">
        {features.map((feature, index) => (
          <div key={feature.id} className="p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Feature {index + 1}</h3>
            </div>

            <div className="space-y-4">
              {/* Icon Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon
                </label>
                <div className="relative">
                  <button
                    onClick={() => setShowIconPicker(showIconPicker === feature.id ? null : feature.id)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:border-[#EF579A] transition-colors flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#EF579A] rounded-lg flex items-center justify-center">
                        <i className={`${feature.icon} text-white text-xl`}></i>
                      </div>
                      <span className="text-sm text-gray-700">{feature.icon}</span>
                    </div>
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </button>

                  {showIconPicker === feature.id && (
                    <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                      <p className="text-xs font-medium text-gray-700 mb-3">Select an icon:</p>
                      <div className="grid grid-cols-6 gap-2">
                        {popularIcons.map((icon) => (
                          <button
                            key={icon}
                            onClick={() => handleIconSelect(feature.id, icon)}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                              feature.icon === icon
                                ? 'bg-[#EF579A] text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <i className={`${icon} text-xl`}></i>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={feature.title}
                  onChange={(e) => handleFeatureChange(feature.id, 'title', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EF579A] focus:border-transparent text-sm"
                  placeholder="Enter feature title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={feature.description}
                  onChange={(e) => handleFeatureChange(feature.id, 'description', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EF579A] focus:border-transparent text-sm"
                  placeholder="Enter feature description"
                />
              </div>
            </div>
          </div>
        ))}

        {/* Preview */}
        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-4">Preview:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div key={feature.id} className="text-center">
                <div className="w-16 h-16 bg-[#EF579A] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className={`${feature.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">{feature.title}</h3>
                <p className="text-xs text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleReset}
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2.5 bg-[#EF579A] text-white rounded-lg font-medium text-sm hover:bg-[#D94A85] transition-colors whitespace-nowrap cursor-pointer"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
